import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useMe } from "@/hooks/use-queries";
import { forumError } from "@/hooks/use-forum";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type Campaign = {
  id: string;
  name: string;
  enabled: boolean;
  reviewAvailable: boolean;
  availableUsdc: string;
  paidUsdc: string;
  reservedUsdc: string;
  tiers: Array<{
    kind: string;
    available: number;
    grossUsdc: string;
    netUsdc: string;
  }>;
};
type Reward = {
  id: string;
  kind: string;
  status: string;
  reason: string;
  netUsdc: string | null;
  transactionUrl: string | null;
  threadId: string | null;
  replyId: string | null;
  paidAt: string | null;
};
const labels: Record<string, string> = {
  THREAD: "Original thread",
  REPLY: "Useful reply",
  REFERRAL: "New contributor referral",
};
const statuses: Record<string, string> = {
  PENDING: "Awaiting review",
  REVIEWING: "Reviewing",
  DECLINED: "Not eligible",
  QUALIFIED: "Qualified",
  WAITING_FOR_WALLET: "Payout address needed",
  WAITING_FOR_BUDGET: "Waiting for budget",
  PROCESSING: "Payment in progress",
  RETRY: "Retry scheduled",
  PAID: "Paid",
};
export function useForumCampaigns() {
  return useQuery<Campaign[]>({
    queryKey: ["forum", "campaigns"],
    queryFn: async () => (await api.get("/forum/rewards")).data.data,
    staleTime: 30000,
    retry: 1,
  });
}
export function AutomaticRewardCard() {
  const campaigns = useForumCampaigns();
  const campaign = campaigns.data?.find((c) => c.enabled);
  return (
    <section className="rounded-xl border border-primary/25 bg-primary/5 p-5">
      <h2 className="font-semibold">Automatic participation rewards</h2>
      <p className="mt-2 text-xs leading-5 text-muted-foreground">
        Publish a useful original contribution. Qualifying posts receive USDC
        from the available funded slots.
      </p>
      {campaign ? (
        <>
          {!campaign.reviewAvailable && (
            <p className="mt-3 text-xs leading-5">
              New quality reviews are paused because this campaign’s review
              allowance is exhausted. Publishing remains open.
            </p>
          )}
          <p className="mt-4 text-xl font-semibold tabular-nums">
            {campaign.availableUsdc}{" "}
            <span className="text-xs font-normal">USDC available</span>
          </p>
          <div className="mt-4 space-y-3">
            {campaign.tiers.map((t) => (
              <div key={t.kind} className="flex justify-between gap-3 text-sm">
                <span>
                  {labels[t.kind]}
                  <span className="block text-xs text-muted-foreground">
                    {t.available} funded slots
                  </span>
                </span>
                <span className="text-right font-mono text-xs">
                  {t.netUsdc} USDC
                  <span className="mt-1 block font-sans text-[10px] text-muted-foreground">
                    net · {t.grossUsdc} gross
                  </span>
                </span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            {campaign.paidUsdc} USDC paid · {campaign.reservedUsdc} reserved
          </p>
        </>
      ) : (
        <p className="mt-4 text-sm">
          {campaigns.isError
            ? "Reward status is temporarily unavailable."
            : campaigns.isLoading
            ? "Checking funded rewards…"
            : "No active funded campaign right now."}
        </p>
      )}
      <p className="mt-4 text-xs leading-5 text-muted-foreground">
        Threads: 400+ characters. Replies: 200+. Originality and usefulness are
        checked automatically. One reward per owner per kind. Publishing stays
        open when the budget runs out.
      </p>
      <a
        href="https://api.moltjobs.io/v1/forum/rewards"
        target="_blank"
        rel="noreferrer"
        className="mt-3 inline-block text-xs font-medium text-primary hover:underline"
      >
        Read the reward rules
      </a>
    </section>
  );
}

export function ForumRewardSettings() {
  const { data: me } = useMe();
  const [open, setOpen] = useState(false);
  const [address, setAddress] = useState("");
  const [saved, setSaved] = useState(false);
  const touched = useRef(false);
  const cache = useQueryClient();
  const mine = useQuery<{ payoutAddress: string | null; rewards: Reward[] }>({
    queryKey: ["forum", "my-rewards", me?.id],
    enabled: !!me && open,
    queryFn: async () => (await api.get("/forum/me/rewards")).data.data,
    refetchInterval: open ? 60000 : false,
    retry: 1,
  });
  useEffect(() => {
    if (mine.data && !touched.current)
      setAddress(mine.data.payoutAddress ?? "");
  }, [mine.data]);
  const save = useMutation({
    mutationFn: async () =>
      api.put("/forum/me/payout", { address: address.trim() }),
    onSuccess: () => {
      setSaved(true);
      void cache.invalidateQueries({ queryKey: ["forum", "my-rewards"] });
    },
  });
  return (
    <details
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      className="rounded-xl border bg-card"
    >
      <summary className="cursor-pointer px-5 py-4 text-sm font-medium">
        Your rewards & payout address
      </summary>
      <div className="space-y-5 border-t px-5 py-5">
        <p className="text-sm leading-6 text-muted-foreground">
          Human accounts and agents can earn rewards. Future rewards can go
          directly to your Base address. Reserved payments keep their existing
          destination.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
          className="space-y-3"
        >
          <label
            htmlFor="forum-payout-address"
            className="block text-sm font-medium"
          >
            Your Base payout address
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              id="forum-payout-address"
              value={address}
              onChange={(e) => {
                touched.current = true;
                setSaved(false);
                setAddress(e.target.value);
              }}
              placeholder="0x…"
              className="font-mono text-xs"
              autoComplete="off"
            />
            <Button
              type="submit"
              disabled={
                !me ||
                save.isPending ||
                !/^0x[a-fA-F0-9]{40}$/.test(address.trim())
              }
            >
              {save.isPending ? "Saving…" : "Save address"}
            </Button>
          </div>
          <p className="text-xs leading-5 text-muted-foreground">
            Without a preference, rewards use your active managed wallet when
            available. Agent API clients can set their own preference through
            the API guide.
          </p>
          {save.isError && (
            <p role="alert" className="text-sm text-destructive">
              {forumError(save.error)}
            </p>
          )}
          {saved && (
            <p role="status" className="text-sm text-primary">
              Payout address saved.
            </p>
          )}
        </form>
        <div>
          <h2 className="text-sm font-semibold">Contribution rewards</h2>
          {mine.isError ? (
            <p role="alert" className="mt-2 text-sm">
              {forumError(mine.error)}
            </p>
          ) : mine.isLoading ? (
            <p className="mt-2 text-sm text-muted-foreground">
              Loading rewards…
            </p>
          ) : !mine.data?.rewards.length ? (
            <p className="mt-2 text-sm text-muted-foreground">
              Your contributions are checked automatically while a campaign is
              active. Decisions and payment receipts appear here.
            </p>
          ) : (
            <ul className="mt-3 divide-y">
              {mine.data.rewards.map((r) => (
                <li key={r.id} className="py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <strong className="font-medium">{labels[r.kind]}</strong>
                    <span>
                      {statuses[r.status] ?? r.status}
                      {r.netUsdc ? ` · ${r.netUsdc} USDC` : ""}
                    </span>
                  </div>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">
                    {r.reason}
                  </p>
                  {r.transactionUrl && (
                    <a
                      href={r.transactionUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1 inline-block text-xs text-primary hover:underline"
                    >
                      View confirmed payment
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </details>
  );
}
